import type React from "react";
import { Text, Tooltip, TooltipPopup, TooltipTrigger } from "@salestrak/ui";

import { useWorkspace } from "#app/lib/data/workspace-context";
import { useAppNavigate } from "#app/lib/hooks/use-app-navigate";
import { usePermission } from "#app/lib/hooks/use-permission";

const TRASH_ROUTE = "/settings/trash";

/**
 * Whether the viewer can open the Trash page at all — the ClickUp member
 * model: `trash.manage` holds the workspace's trash, and anyone who can
 * delete an entity opens the page to restore their own deletions.
 */
export function useCanOpenTrash(): boolean {
  const canManageTrash = usePermission("trash.manage");
  const canDeleteItems = usePermission("delete.items");
  return canManageTrash || canDeleteItems;
}

/**
 * THE toast title for a delete: "<subject> moved to trash", where "trash" is
 * a link to the Trash page for a viewer who can open it and plain text for
 * one who cannot. `subject` is the KIND ("Project", "3 projects", "Contact"),
 * never the entity's name — the toast confirms what happened, and the name
 * is one click away in the trash. The link wears a tooltip naming where it
 * goes: an underlined word in a sentence says "link", not which page.
 * Renders inside the toast portal, so it reads the navigator and
 * permissions from the provider stack like any other screen.
 */
export function MovedToTrashTitle({
  subject,
}: {
  subject: string;
}): React.ReactElement {
  const canOpenTrash = useCanOpenTrash();
  const { push } = useAppNavigate();
  const { activeWorkspace } = useWorkspace();

  return (
    <>
      {subject} moved to{" "}
      {canOpenTrash ? (
        <Tooltip>
          <TooltipTrigger
            render={
              <Text
                as="a"
                // A real href, so a modifier-click or a right-click opens
                // the page the browser's way; a plain press stays in the SPA.
                href={`/${activeWorkspace?.urlPath ?? ""}${TRASH_ROUTE}`}
                onClick={(event) => {
                  if (
                    event.button !== 0 ||
                    event.metaKey ||
                    event.ctrlKey ||
                    event.shiftKey ||
                    event.altKey
                  ) {
                    return;
                  }
                  event.preventDefault();
                  push(TRASH_ROUTE);
                }}
                underline
              >
                trash
              </Text>
            }
          />
          <TooltipPopup>Open Trash</TooltipPopup>
        </Tooltip>
      ) : (
        "trash"
      )}
    </>
  );
}
